import { Button, Stack, Text } from '@chakra-ui/react'
import { faFlag } from '@fortawesome/pro-regular-svg-icons'
import { faCheck, faVideo, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { getSessionJoinRedirectUrl } from '@/client/services/url'
import type { PostStatus } from '@/client/services/worlds'

import type { PostDetails } from '@/components/Community/Post/types'
import ReportModalV2 from '@/components/Report/ReportModalV2'
import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import Divider from '@/components/Worlds/UI/Divider'

import LiveHelpSessionLabel from './LiveHelpSessionLabel'
import type { LiveHelpRequestWithSessionDetails } from './types'

export default function AuthorReceivedLiveHelpBanner({
  post,
  liveHelpRequest,
  postIsFullView,
}: {
  post: PostDetails
  liveHelpRequest: LiveHelpRequestWithSessionDetails
  postIsFullView: boolean
}): JSX.Element {
  const toast = useToaster()
  const [showReportModal, setShowReportModal] = useState(false)

  const { session } = liveHelpRequest
  const { host } = session

  const [updatePostStatus, isUpdating] = useAPIWrapper(
    async (status: PostStatus) => {
      await RPC.updatePostStatus({
        postId: post.id,
        status,
        completedLiveHelpRequestId: liveHelpRequest.id,
      })
      toast({
        title:
          status === 'solved'
            ? 'Nice! You marked this question as solved'
            : 'You marked this question as not solved',
        status: 'success',
      })
    },
    [liveHelpRequest.id, post.id, toast],
    'Update Post Status'
  )

  return (
    <Stack
      direction="column"
      width="100%"
      p="16px"
      bgColor="bg.subtle"
      borderRadius="8px"
      spacing="16px"
    >
      <ReportModalV2
        entity={{ type: 'session-host', id: host.id }}
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        reportedUserName={host.user.displayName}
      />
      <Stack direction="row" justify="space-between" align="left">
        <LiveHelpSessionLabel user={host.user} />
        <Stack
          direction={{ base: 'column', xl: postIsFullView ? 'row' : 'column' }}
          flexShrink={0}
          spacing={{ base: 0, xl: postIsFullView ? '24px' : 0 }}
          alignItems="flex-start"
        >
          <ShwLink href={getSessionJoinRedirectUrl(session.id, false)} isExternal>
            <Button variant="ghost" leftIcon={<FontAwesomeIcon icon={faVideo} />}>
              Join Zoom Room
            </Button>
          </ShwLink>
          <Button
            variant="ghost"
            onClick={() => setShowReportModal(true)}
            leftIcon={<FontAwesomeIcon icon={faFlag} />}
          >
            Share Safety Concern
          </Button>
        </Stack>
      </Stack>

      <Divider />

      <Stack direction={{ base: 'column', lg: 'row' }} justify="space-between" align="center">
        <Text textAlign="center" fontWeight="bold">
          Did {host.user.displayName} help you answer your question?
        </Text>
        <Stack direction="row" flexShrink={0}>
          <Button
            onClick={() => updatePostStatus('solved')}
            variant="outline"
            isDisabled={isUpdating}
            leftIcon={<FontAwesomeIcon icon={faCheck} />}
          >
            Yes
          </Button>
          <Button
            onClick={() => updatePostStatus('not-solved')}
            variant="outline"
            isDisabled={isUpdating}
            leftIcon={<FontAwesomeIcon icon={faXmark} />}
          >
            No
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}
